import React, { useEffect, useState, useCallback } from "react";
import { Modal, Button } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../utils/helper";

import { useInterval } from "bx-react-ui/src/utils/hooks";
import { CircularProgress } from "../components/UI/CircularProgress/CircularProgress";
import {
  getCurrentAccount,
  getAuthorizationsLoaded,
  getNotifications,
  getNotificationsLoaded,
  getNotificationsLoading,
  getSession,
  getTexts,
  getTextsLoading,
} from "../reducers/selectors";
import { getAuthorizations } from "../services/api/authorizations";
import { getLegalContracts, setLegalContract } from "../services/api/person";
import { getMessages } from "../services/api/internalMessages";
import {
  authorizationsSuccess,
  notificationsSuccess,
  setNotificationsLoading,
  textsSet,
  textsLoadingSet,
} from "../actions/entities";
import { PopupWrapper } from "../components/PopupWrapper/PopupWrapper";
import { fetchTooltipsAndNotes, getFeatureFlags } from "../services/api/entities";

const updateAuthorizations = (dispatch) => {
  getAuthorizations()
    .then(({ response }) => {
      const newAuthorizations = response.authorizations.filter((authItem) => authItem.processedBy === "");
      dispatch(authorizationsSuccess(newAuthorizations));
    })
    .catch((err) => {
      console.error(err);
      showToast("Error obteniendo autorizaciones del servidor", true);
      dispatch(authorizationsSuccess([]));
    });
};

export const AppInfoWrapper = React.memo(({ children }) => {
  const dispatch = useDispatch();

  const authorizationsLoaded = useSelector(getAuthorizationsLoaded);
  const notificationsLoaded = useSelector(getNotificationsLoaded);
  const notificationsLoading = useSelector(getNotificationsLoading);
  const notifications = useSelector(getNotifications);
  const currentAccount = useSelector(getCurrentAccount);

  const session = useSelector(getSession);
  const textsLoading = useSelector(getTextsLoading);
  const [contract, setContract] = useState(null);
  const [contractLoading, setContractLoading] = useState(false);
  const [contractLoaded, setContractLoaded] = useState(false);
  const [loadingAbsolute, setLoadingAbsolute] = useState(true);
  const [isFirstAccess, setIsFirstAccess] = useState(false);

  useEffect(() => {
    if (currentAccount.id) {
      getFeatureFlagsAsync();
    }
  }, [currentAccount]);

  const getFeatureFlagsAsync = async () => {
    try {
      const { response } = await getFeatureFlags(window.___APP_CONFIG___.APP_MOBILE ? "App" : "Web");
      const { SDTClientX_Features, result } = response;
      if (result.Code === 200) {
        window.___APP_CONFIG___.FEATURE_FLAGS = SDTClientX_Features;
      } else {
        window.___APP_CONFIG___.FEATURE_FLAGS = {};
        console.error("Error fetching feature flags:", result.Description);
      }
    } catch (error) {
      console.error("Error fetching feature flags:", error);
    }
  };

  useEffect(() => {
    if (notifications) {
      notifications.map((notification) => {
        if (notification.InternalMessageForm === "FirstAccess") {
          setIsFirstAccess(() => true);
          setLoadingAbsolute(() => false);
        }
      });
    }
  }, [notifications]);

  useEffect(() => {
    if (session && !textsLoading) {
      dispatch(textsLoadingSet());
      fetchTooltipsAndNotes()
        .then(({ response }) => {
          if (response.result.Code === 200) {
            dispatch(textsSet({ tooltips: response.tooltips, notes: response.notes }));
          }
        })
        .catch((err) => {
          console.error(err);
        });
    }
  }, [session, textsLoading]);

  useEffect(() => {
    if (!contractLoading && session && notifications && !contract && !contractLoaded) {
      updateLegalContract();
    }
  }, [session]);

  useEffect(() => {
    if (!notificationsLoaded && !notificationsLoading && session) {
      updateMessages();
    }
  }, [session, notificationsLoaded, notificationsLoading]);

  useEffect(() => {
    if ((notificationsLoaded && !notificationsLoading && authorizationsLoaded && session) || !session) {
      setLoadingAbsolute(() => false);
    }
  }, [session, notificationsLoaded, notificationsLoading, authorizationsLoaded]);

  const updateMessages = () => {
    dispatch(setNotificationsLoading(true));
    getMessages()
      .then(({ response }) => {
        dispatch(notificationsSuccess(response.InternalMessageSDTList));
      })
      .catch((err) => {
        console.error(err);
        showToast("Error obteniendo notificaciones del servidor", true);
        dispatch(notificationsSuccess([]));
      });
  };

  const updateLegalContract = () => {
    let stopCheckContract = false;
    notifications.map((notificationItem) => {
      if (notificationItem.InternalMessageForm === "FirstAccess") {
        stopCheckContract = true;
      }
    });

    if (!stopCheckContract) {
      setContractLoading(() => true);
      getLegalContracts()
        .then(({ response }) => {
          if (response.result.Code !== 200) {
            showToast("Error obteniendo nuevos contratos del servidor", true);
          } else {
            if (response.LegalContractSDT.LegalContractAttachmentName) {
              setContract({
                ...response.LegalContractSDT,
                LegalContractAttachmentBlob: response.blobfile,
              });
            }
            setContractLoaded(() => true);
          }
        })
        .catch((err) => {
          console.error(err);
          showToast("Error obteniendo nuevos contratos del servidor", true);
        })
        .finally(() => {
          setContractLoading(() => false);
        });
    } else {
      setContract(null);
      setContractLoading(() => false);
    }
  };

  if (loadingAbsolute)
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
          height: "100vh",
        }}
      >
        <Authorizations />
        <CircularProgress />
      </div>
    );

  if (isFirstAccess) {
    return <PopupWrapper>{children}</PopupWrapper>;
  }

  return (
    <React.Fragment>
      <Authorizations />
      {children}
    </React.Fragment>
  );
});

const Authorizations = () => {
  const session = useSelector(getSession);
  const authorizationsLoaded = useSelector(getAuthorizationsLoaded);
  const [authorizationsLoading, setAuthorizationsLoading] = useState(false);
  const dispatch = useDispatch();

  useInterval(() => {
    if (session) {
      updateAuthorizations(dispatch);
    }
  }, 30e4);

  useEffect(() => {
    if (!authorizationsLoaded && session && !authorizationsLoading) {
      setAuthorizationsLoading(() => true);
      updateAuthorizations(dispatch);
    }
  }, [session, authorizationsLoaded, authorizationsLoading]);

  return null;
};
